import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { useConfig } from "../../hooks/useConfig";
import { Plus, Search, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AccessBlocked } from "../../components/AccessBlocked";

export default function CRMBoardPage() {
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const verticals = getValues("crm_verticals");
  const [vertical, setVertical] = useState("");
  const [board, setBoard] = useState({ pipeline: null, stages: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [blocked, setBlocked] = useState("");

  useEffect(() => { if (verticals.length && !vertical) setVertical(verticals[0]); }, [verticals, vertical]);

  const load = useCallback(() => {
    if (!vertical) return;
    setLoading(true);
    setErr("");
    setBlocked("");
    api.get(`/crm/board?vertical=${encodeURIComponent(vertical)}`)
      .then(r => setBoard(r.data || { pipeline: null, stages: [] }))
      .catch(e => {
        // FX3b item 8 — a 403 never shows the raw permission key the server names
        if (e.response?.status === 403) { setBlocked(e.response?.data?.detail || "forbidden"); return; }
        setErr(e.response?.data?.detail || "Failed to load the board");
      })
      .finally(() => setLoading(false));
  }, [vertical]);

  useEffect(() => { load(); }, [load]);

  if (blocked) return <div className="p-8"><AccessBlocked detail={blocked} testid="crm-access-blocked" /></div>;
  if (err) return <div className="p-8 text-center"><p className="text-zinc-500 text-sm">{err}</p></div>;

  const handleDrop = async (partyId, newStageKey) => {
    try {
      await api.put(`/crm/parties/${partyId}`, { current_stage_key: newStageKey });
      toast.success("Stage updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="crm-board-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Pipeline Board</h1>
          <p className="text-sm text-zinc-500">Drag parties across stages. {board.pipeline?.name || ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={vertical} onValueChange={setVertical}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="vertical-select"><SelectValue placeholder="Vertical..." /></SelectTrigger>
            <SelectContent>{verticals.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {loading ? <p className="text-xs text-zinc-400">Loading...</p> : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="board-columns">
          {board.stages.map(stage => (
            <div key={stage.key} className="min-w-[260px] max-w-[280px] bg-zinc-50 rounded-md border border-zinc-200 flex-shrink-0"
              onDragOver={e => e.preventDefault()}
              onDrop={e => { const pid = e.dataTransfer.getData("partyId"); if (pid) handleDrop(pid, stage.key); }}>
              <div className="px-3 py-2 border-b border-zinc-200 bg-white rounded-t-md">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-zinc-700">{stage.name}</h3>
                  <span className="text-[10px] font-mono text-zinc-400">{stage.count}</span>
                </div>
                {stage.total_value_paise > 0 && <p className="text-[10px] text-zinc-400 mt-0.5">Value: {(stage.total_value_paise / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}</p>}
              </div>
              <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                {stage.parties.map(p => (
                  <div key={p._id} draggable onDragStart={e => e.dataTransfer.setData("partyId", p._id)}
                    className="bg-white border border-zinc-100 rounded-sm p-2 cursor-grab hover:shadow-sm transition-shadow"
                    onClick={() => navigate(`/crm/party/${p._id}`)} data-testid={`party-card-${p._id}`}>
                    <p className="text-xs font-semibold text-zinc-900 truncate">{p.name}</p>
                    {p.company_name && <p className="text-[10px] text-zinc-500 truncate">{p.company_name}</p>}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-zinc-400">{p.source}</span>
                      {p.expected_value_paise > 0 && <span className="text-[10px] font-mono text-emerald-600">{(p.expected_value_paise / 100).toLocaleString("en-IN")}</span>}
                    </div>
                  </div>
                ))}
                {stage.parties.length === 0 && <p className="text-[10px] text-zinc-300 text-center py-4">No parties</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
